import { useEffect, useRef, useState } from 'react';
import type { Product } from '@/api/products';
import { composeMockupImage } from '@/lib/mockupComposite';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import { useDirectSaleMockupInput } from '../useDirectSaleMockupInput';

export type DirectSaleShareOutcome = 'saved' | 'failed';

/** Surface behind the product in the shared image — the editor's ground. */
const SHARE_BACKGROUND = '#E4E7F0';

/**
 * Sharing on the desktop, the same way the phone's "more" sheet does it: a
 * picture of the product with the design on it, never a link — the frame's
 * own URL carries the shop signature.
 *
 * The picture is composed as soon as a design exists, not on the click:
 * browsers only let `share()` run inside the user gesture, and loading the
 * layers takes longer than that. Without a design there is nothing to share
 * and `canShare` stays false.
 */
export function useDirectSaleShare(product: Product): {
  /** A composed image is ready; without it the button is disabled. */
  canShare: boolean;
  /** Synchronous up to navigator.share — call it directly in the click handler. */
  share: () => void;
  outcome: DirectSaleShareOutcome | null;
} {
  const [outcome, setOutcome] = useState<DirectSaleShareOutcome | null>(null);
  const [picture, setPicture] = useState<File | null>(null);

  // The view the customer is looking at — or, if that one is empty, the
  // first one that carries a design.
  const activePosition = useProductSelectionStore((s) => s.activePosition);
  const masterPositions = useCanvasStateManager((s) => s.masterPositions);
  const shareSlug =
    (masterPositions[activePosition]?.preview
      ? activePosition
      : (product.positions ?? []).find((p) => masterPositions[p.slug]?.preview)
          ?.slug) ?? activePosition;
  const designUrl = masterPositions[shareSlug]?.preview ?? null;
  const mockup = useDirectSaleMockupInput(product, shareSlug);

  const mockupRef = useRef(mockup);
  mockupRef.current = mockup;
  const templateId = mockup.template?.id ?? null;
  useEffect(() => {
    // A picture of another design, colour or view must not be shared as
    // the current one, and a verdict on it says nothing about the next.
    setPicture(null);
    setOutcome(null);
    const { template, ...rest } = mockupRef.current;
    if (!template || !designUrl) return;
    let live = true;
    composeMockupImage({
      template,
      ...rest,
      designUrl,
      frameBackground: SHARE_BACKGROUND,
    }).then(
      (blob) => {
        if (!live) return;
        setPicture(new File([blob], 'design.png', { type: 'image/png' }));
      },
      // No picture, no sharing: the button stays disabled.
      () => undefined
    );
    return () => {
      live = false;
    };
  }, [templateId, designUrl, mockup.backgroundColor]);

  /** No share sheet here (a desktop, a frame without permission): save it. */
  const save = (file: File) => {
    try {
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setOutcome('saved');
    } catch {
      setOutcome('failed');
    }
  };

  // Synchronous up to share(): still inside the click.
  const share = () => {
    if (!picture) {
      setOutcome('failed');
      return;
    }
    setOutcome(null);
    const data = { title: product.name, files: [picture] };
    // Inside a shop's iframe this needs allow="web-share" on the frame.
    if (navigator.canShare?.(data)) {
      navigator.share(data).catch((error: { name?: string }) => {
        // The customer closed the share sheet: nothing to report.
        if (error?.name !== 'AbortError') save(picture);
      });
      return;
    }
    save(picture);
  };

  return { canShare: picture !== null, share, outcome };
}
